import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TokenService } from '@admin/auth/services/token.service';
import { AccessTokenResponse } from '@admin/auth/interfaces/auth-token-response.interface';
import { environment } from '@admin/env/environment';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';

const REFRESH_PATH = '/api/refresh';

let isRefreshing = false;
const refreshedAccessToken$ = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const tokenService = inject(TokenService);
    const router = inject(Router);
    const http = inject(HttpClient);

    const isRefreshRequest = req.url.includes(REFRESH_PATH);
    const accessToken = tokenService.getAccessToken();
    const outgoing = !isRefreshRequest && accessToken ? withBearer(req, accessToken) : req;

    return next(outgoing).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401 || isRefreshRequest) {
                if (error.status === 401 && isRefreshRequest) {
                    void clearAndRedirect(tokenService, router);
                }
                return throwError(() => error);
            }
            return refreshAndRetry(req, next, tokenService, router, http);
        }),
    );
};

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function refreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    tokenService: TokenService,
    router: Router,
    http: HttpClient,
): Observable<HttpEvent<unknown>> {
    if (isRefreshing) {
        return refreshedAccessToken$.pipe(
            filter((token): token is string => token !== null),
            take(1),
            switchMap((token) => next(withBearer(req, token))),
        );
    }

    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) {
        void clearAndRedirect(tokenService, router);
        return throwError(() => new Error('No refresh token available'));
    }

    isRefreshing = true;
    refreshedAccessToken$.next(null);

    return http.post<AccessTokenResponse>(`${environment.BASE_URL}${REFRESH_PATH}`, { refreshToken: refreshToken }).pipe(
        switchMap((response) => {
            tokenService.setTokens(response.accessToken, response.refreshToken);
            isRefreshing = false;
            refreshedAccessToken$.next(response.accessToken);
            return next(withBearer(req, response.accessToken));
        }),
        catchError((err) => {
            isRefreshing = false;
            refreshedAccessToken$.next(null);
            void clearAndRedirect(tokenService, router);
            return throwError(() => err);
        }),
    );
}

async function clearAndRedirect(tokenService: TokenService, router: Router): Promise<void> {
    tokenService.clear();
    await router.navigate(['/auth/login']);
}
